import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Target, Users, Calendar, BarChart3, 
  CheckCircle, MessageSquare, Brain, Award, Rocket,
  TrendingUp, FileText, Lightbulb
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart as RechartsPieChart, Cell, Pie, AreaChart, Area } from 'recharts';

interface Project {
  id: string;
  title: string;
  type: 'Nouveau Client' | 'Projet TI' | 'Infrastructure' | 'Innovation' | 'Formation';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Planifié' | 'En cours' | 'En attente' | 'Complété';
  client: string;
  description: string;
  team: Array<{
    name: string;
    role: 'A' | 'C' | 'R' | 'I'; // Accountable, Consulted, Responsible, Informed
    avatar: string;
  }>;
  progress: number;
  startDate: string;
  endDate: string;
  budget: number;
  tasks: number;
  kpis: {
    completion: number;
    budget: number;
    quality: number;
    communication: number;
  };
  phases: Array<{
    name: string;
    status: 'Completed' | 'In Progress' | 'Planned';
    progress: number;
  }>;
  aiInsights: string[];
  communication: {
    frequency: string;
    channels: string[];
    satisfaction: number;
  };
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface ChargeProjetTactiCeProps {
  isOpen: boolean;
  onClose: () => void;
}

const mockProjects: Project[] = [
  {
    id: 'projet-naomie',
    title: 'Projet Naomie',
    type: 'Nouveau Client',
    priority: 'Medium',
    status: 'Planifié',
    client: 'BZ Inc. Entreprise',
    description: 'Travailler avec Naomie pour l\'inclure dans les Plans tactiques et optimiser sa contribution',
    team: [
      { name: 'Émilie', role: 'A', avatar: 'E' },
      { name: 'Dany', role: 'C', avatar: 'D' },
      { name: 'Naomie', role: 'R', avatar: 'N' }
    ],
    progress: 45,
    startDate: '25/09/2025',
    endDate: '30/12/2025',
    budget: 85000,
    tasks: 15,
    kpis: {
      completion: 45,
      budget: 92,
      quality: 88,
      communication: 95
    },
    phases: [
      { name: 'Analyse des besoins', status: 'Completed', progress: 100 },
      { name: 'Planification tactique', status: 'In Progress', progress: 60 },
      { name: 'Formation et intégration', status: 'Planned', progress: 0 },
      { name: 'Suivi et optimisation', status: 'Planned', progress: 0 }
    ],
    aiInsights: [
      'Communication excellente avec le client (95%)',
      'Progression légèrement en avance sur le planning',
      'Recommandation: Intensifier les sessions de formation'
    ],
    communication: {
      frequency: 'Hebdomadaire',
      channels: ['Teams', 'Email', 'Réunions'],
      satisfaction: 95
    }
  },
  {
    id: 'datacenter-bz',
    title: 'Projet Datacenter BZ',
    type: 'Infrastructure',
    priority: 'High',
    status: 'En cours',
    client: 'BZ Inc. Entreprise',
    description: 'Plan d\'actions et de suivi sur l\'avancement des nouvelles salles cloud de BZ. Oricom doit changer d\'endroit. BZ Père-Lelièvre en 2e.',
    team: [
      { name: 'François', role: 'A', avatar: 'F' },
      { name: 'Dany', role: 'R', avatar: 'D' },
      { name: 'Naomie', role: 'R', avatar: 'N' }
    ],
    progress: 16,
    startDate: '15/09/2025',
    endDate: '29/04/2027',
    budget: 450000,
    tasks: 27,
    kpis: {
      completion: 16,
      budget: 78,
      quality: 85,
      communication: 88
    },
    phases: [
      { name: 'Audit infrastructure', status: 'Completed', progress: 100 },
      { name: 'Planification migration', status: 'In Progress', progress: 30 },
      { name: 'Migration Oricom', status: 'Planned', progress: 0 },
      { name: 'Optimisation BZ Père-Lelièvre', status: 'Planned', progress: 0 }
    ],
    aiInsights: [
      'Projet complexe nécessitant une communication renforcée',
      'Risques identifiés sur la migration Oricom',
      'Recommandation: Réunions quotidiennes pendant la phase critique'
    ],
    communication: {
      frequency: 'Quotidienne',
      channels: ['Teams', 'Slack', 'Réunions', 'Dashboard'],
      satisfaction: 88
    }
  },
  {
    id: 'plan-db-fd',
    title: 'Plan DB & FD',
    type: 'Projet TI',
    priority: 'Medium',
    status: 'En cours',
    client: 'BZ Inc. Entreprise',
    description: 'Plan stratégique pour l\'optimisation des bases de données et la formation des équipes',
    team: [
      { name: 'Dany', role: 'A', avatar: 'D' },
      { name: 'François', role: 'R', avatar: 'F' }
    ],
    progress: 42,
    startDate: '31/12/2024',
    endDate: '30/12/2025',
    budget: 120000,
    tasks: 33,
    kpis: {
      completion: 42,
      budget: 85,
      quality: 90,
      communication: 92
    },
    phases: [
      { name: 'Audit DB existantes', status: 'Completed', progress: 100 },
      { name: 'Plan d\'optimisation', status: 'In Progress', progress: 70 },
      { name: 'Formation équipes', status: 'In Progress', progress: 20 },
      { name: 'Mise en œuvre', status: 'Planned', progress: 0 }
    ],
    aiInsights: [
      'Performance DB améliorée de 35%',
      'Formation équipes très bien accueillie',
      'Recommandation: Accélérer la phase de mise en œuvre'
    ],
    communication: {
      frequency: 'Bi-hebdomadaire',
      channels: ['Teams', 'Email', 'Formation'],
      satisfaction: 92
    }
  }
];

const createSlides = (): Slide[] => [
  {
    id: 'concept',
    title: 'TactiCe - Machine à Saucisse BZ',
    subtitle: 'Tous les projets entrent dans l\'outil BZ - Concept simple et efficace',
    icon: Target,
    color: '#0066FF',
    content: (
      <div className="space-y-6">
        {/* Header Concept */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Target className="w-8 h-8" />
            TactiCe - La Machine à Saucisse BZ
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">100%</div>
              <div className="text-white/90">Projets dans BZ</div>
              <div className="text-sm text-white/80">Obligatoire</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">AI</div>
              <div className="text-white/90">Analyse & Planning</div>
              <div className="text-sm text-white/80">Automatisé</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">95%</div>
              <div className="text-white/90">Satisfaction Client</div>
              <div className="text-sm text-white/80">Communication</div>
            </div>
          </div>
        </div>

        {/* Concept Simple */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border-l-4 border-green-500">
          <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-green-600" />
            Concept Simple - La Machine à Saucisse
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Règle d'Or BZ</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <div>
                    <div className="font-medium text-gray-800">Nouveau Client</div>
                    <div className="text-sm text-gray-600">→ Projet dans TactiCe</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <div>
                    <div className="font-medium text-gray-800">Projet TI</div>
                    <div className="text-sm text-gray-600">→ Projet dans TactiCe</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
                  <div>
                    <div className="font-medium text-gray-800">Tout Autre Projet</div>
                    <div className="text-sm text-gray-600">→ Projet dans TactiCe</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Avantages du Concept</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Visibilité complète sur tous les projets
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  KPIs et métriques unifiées
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Communication centralisée
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Planning AI automatisé
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Suivi en temps réel
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Philosophie BZ */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border-l-4 border-orange-500">
          <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-orange-600" />
            Philosophie BZ - 25 ans d'Excellence
          </h3>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">
              "Fix the Customer than fix the problem"
            </div>
            <p className="text-gray-700 text-lg">
              La communication est la clé du succès. Depuis 25 ans, BZ privilégie toujours 
              la relation client avant la solution technique.
            </p>
          </div>
        </div>

        {/* Types de Projets */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Types de Projets Gérés par TactiCe
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { type: 'Nouveau Client', icon: '👥', color: 'bg-blue-500', description: 'Intégration et onboarding' },
              { type: 'Projet TI', icon: '💻', color: 'bg-green-500', description: 'Développement et infrastructure' },
              { type: 'Infrastructure', icon: '🏗️', color: 'bg-purple-500', description: 'Cloud et datacenter' },
              { type: 'Innovation', icon: '🚀', color: 'bg-orange-500', description: 'R&D et POC' },
              { type: 'Formation', icon: '🎓', color: 'bg-pink-500', description: 'Compétences et certification' },
              { type: 'Support', icon: '🛠️', color: 'bg-gray-500', description: 'Maintenance et support' }
            ].map((project, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 ${project.color} rounded-lg flex items-center justify-center text-white text-xl`}>
                    {project.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{project.type}</h4>
                    <p className="text-sm text-gray-600">{project.description}</p>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  → Automatiquement créé dans TactiCe
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'projects',
    title: 'Projets TactiCe - Interface Vendeuse',
    subtitle: 'Visuels spectaculaires et gestion complète des projets',
    icon: Users,
    color: '#27AE60',
    content: (
      <div className="space-y-6">
        {/* Header Projets */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Users className="w-8 h-8" />
            Projets TactiCe - Interface Vendeuse
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">13</div>
              <div className="text-white/90">Projets Actifs</div>
              <div className="text-sm text-white/80">Sur 15 total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">95%</div>
              <div className="text-white/90">Satisfaction</div>
              <div className="text-sm text-white/80">Communication</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-white/90">Planning</div>
              <div className="text-sm text-white/80">Automatisé</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-white/90">Visibilité</div>
              <div className="text-sm text-white/80">Temps réel</div>
            </div>
          </div>
        </div>

        {/* Projets Cards */}
        <div className="space-y-6">
          {mockProjects.map((project) => (
            <div key={project.id} className="bg-white rounded-xl shadow-lg border overflow-hidden hover:shadow-xl transition-shadow">
              {/* Header avec Image et Labels */}
              <div className="relative h-32 bg-gradient-to-r from-blue-500 to-purple-600">
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    project.priority === 'High' ? 'bg-red-500 text-white' :
                    project.priority === 'Medium' ? 'bg-yellow-500 text-white' :
                    'bg-green-500 text-white'
                  }`}>
                    {project.priority === 'High' ? 'Haute' : 
                     project.priority === 'Medium' ? 'Moyenne' : 'Faible'}
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-white/20 text-white backdrop-blur-sm">
                    {project.type}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className={`w-3 h-3 rounded-full ${
                    project.status === 'En cours' ? 'bg-blue-500' :
                    project.status === 'Planifié' ? 'bg-purple-500' :
                    'bg-green-500'
                  }`}></span>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">{project.title}</h3>
                  <p className="text-white/90 text-sm">{project.status}</p>
                </div>
              </div>

              {/* Contenu Principal */}
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-700 mb-3">{project.description}</p>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      B
                    </div>
                    <span className="text-sm text-gray-600">{project.client}</span>
                  </div>

                  {/* Équipe */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-gray-600">Équipe:</span>
                    {project.team.map((member, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <span className={`px-2 py-1 text-xs rounded ${
                          member.role === 'A' ? 'bg-red-100 text-red-800' :
                          member.role === 'C' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'R' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.role} {member.name}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Progression */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progression</span>
                      <span>{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          project.progress > 70 ? 'bg-green-500' :
                          project.progress > 40 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Calculé à partir des tâches</p>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-600">Début:</span>
                      <span className="ml-2 font-medium">{project.startDate}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Fin prévue:</span>
                      <span className="ml-2 font-medium">{project.endDate}</span>
                    </div>
                  </div>

                  {/* Icons Actions */}
                  <div className="flex items-center gap-3 text-gray-400">
                    <div className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs">{project.tasks}</span>
                    </div>
                    <MessageSquare className="w-4 h-4" />
                    <Brain className="w-4 h-4" />
                    <Calendar className="w-4 h-4" />
                    <BarChart3 className="w-4 h-4" />
                    <Target className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: 'ai-kpis',
    title: 'Analyse AI & KPIs',
    subtitle: 'Intelligence artificielle et métriques de performance',
    icon: Brain,
    color: '#8E44AD',
    content: (
      <div className="space-y-6">
        {/* Header AI */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Brain className="w-8 h-8" />
            Analyse AI & KPIs - Intelligence TactiCe
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">95%</div>
              <div className="text-white/90">Précision AI</div>
              <div className="text-sm text-white/80">Prédictions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">24/7</div>
              <div className="text-white/90">Monitoring</div>
              <div className="text-sm text-white/80">Temps réel</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-white/90">Planning</div>
              <div className="text-sm text-white/80">Automatisé</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-white/90">Visibilité</div>
              <div className="text-sm text-white/80">KPIs</div>
            </div>
          </div>
        </div>

        {/* KPIs Globaux */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              KPIs Globaux des Projets
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mockProjects.map(project => ({
                  project: project.title.split(' ')[1] || project.title,
                  completion: project.kpis.completion,
                  budget: project.kpis.budget,
                  quality: project.kpis.quality,
                  communication: project.kpis.communication
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="project" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="completion" fill="#3498DB" name="Completion %" />
                  <Bar dataKey="budget" fill="#2ECC71" name="Budget %" />
                  <Bar dataKey="quality" fill="#F39C12" name="Quality %" />
                  <Bar dataKey="communication" fill="#E74C3C" name="Communication %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Répartition par Statut
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={[
                      { name: 'En cours', value: 2, color: '#3498DB' },
                      { name: 'Planifié', value: 1, color: '#9B59B6' },
                      { name: 'Complété', value: 0, color: '#2ECC71' },
                      { name: 'En attente', value: 0, color: '#F39C12' }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'En cours', value: 2, color: '#3498DB' },
                      { name: 'Planifié', value: 1, color: '#9B59B6' },
                      { name: 'Complété', value: 0, color: '#2ECC71' },
                      { name: 'En attente', value: 0, color: '#F39C12' }
                    ].map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Insights AI par Projet */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-600" />
            Insights AI par Projet
          </h3>
          <div className="space-y-4">
            {mockProjects.map((project) => (
              <div key={project.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">{project.title}</h4>
                    <p className="text-sm text-gray-600">{project.type} • {project.status}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">{project.progress}%</div>
                    <div className="text-sm text-gray-500">Progression</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">KPIs Performance</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Completion:</span>
                        <span className="text-sm font-medium">{project.kpis.completion}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Budget:</span>
                        <span className="text-sm font-medium">{project.kpis.budget}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Qualité:</span>
                        <span className="text-sm font-medium">{project.kpis.quality}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Communication:</span>
                        <span className="text-sm font-medium text-green-600">{project.kpis.communication}%</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">Communication</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Fréquence:</span>
                        <span className="text-sm font-medium">{project.communication.frequency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Satisfaction:</span>
                        <span className="text-sm font-medium text-green-600">{project.communication.satisfaction}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Canaux:</span>
                        <span className="text-sm font-medium">{project.communication.channels.join(', ')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-600" />
                    Insights AI
                  </h5>
                  <ul className="space-y-1">
                    {project.aiInsights.map((insight, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'communication',
    title: 'Communication - Fix the Customer',
    subtitle: 'La communication est primordiale - 25 ans d\'excellence BZ',
    icon: MessageSquare,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Header Communication */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <MessageSquare className="w-8 h-8" />
            Communication - Fix the Customer than fix the problem
          </h3>
          <div className="text-center">
            <div className="text-3xl font-bold mb-2">25 ans d'Excellence</div>
            <p className="text-white/90 text-lg">
              La communication est la clé du succès. BZ privilégie toujours la relation client avant la solution technique.
            </p>
          </div>
        </div>

        {/* Philosophie Communication */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border-l-4 border-orange-500">
          <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center gap-2">
            <Award className="w-6 h-6 text-orange-600" />
            Philosophie BZ - Communication Primordiale
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Principe Fondamental</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">1. Écouter le Client</div>
                  <div className="text-sm text-gray-600">Comprendre ses besoins réels avant de proposer une solution</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">2. Communiquer Proactivement</div>
                  <div className="text-sm text-gray-600">Anticiper les questions et maintenir la transparence</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">3. Adapter le Message</div>
                  <div className="text-sm text-gray-600">Utiliser le langage et les canaux appropriés</div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Outils TactiCe</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Dashboard Temps Réel</div>
                  <div className="text-sm text-gray-600">Visibilité complète sur l'avancement</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Notifications AI</div>
                  <div className="text-sm text-gray-600">Alertes intelligentes et personnalisées</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Rapports Automatiques</div>
                  <div className="text-sm text-gray-600">Mises à jour régulières et détaillées</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Métriques Communication */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Performance Communication
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[
                  { month: 'Jan', satisfaction: 85, frequency: 80, response: 90 },
                  { month: 'Fév', satisfaction: 88, frequency: 85, response: 92 },
                  { month: 'Mar', satisfaction: 90, frequency: 88, response: 94 },
                  { month: 'Avr', satisfaction: 92, frequency: 90, response: 95 },
                  { month: 'Mai', satisfaction: 94, frequency: 92, response: 96 },
                  { month: 'Jun', satisfaction: 95, frequency: 94, response: 97 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="satisfaction" stackId="1" stroke="#E74C3C" fill="#E74C3C" name="Satisfaction %" />
                  <Area type="monotone" dataKey="frequency" stackId="2" stroke="#3498DB" fill="#3498DB" name="Fréquence %" />
                  <Area type="monotone" dataKey="response" stackId="3" stroke="#2ECC71" fill="#2ECC71" name="Réponse %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Canaux de Communication
            </h3>
            <div className="space-y-4">
              {[
                { channel: 'Teams', usage: 95, satisfaction: 92, description: 'Communication quotidienne' },
                { channel: 'Email', usage: 88, satisfaction: 85, description: 'Documentation formelle' },
                { channel: 'Réunions', usage: 75, satisfaction: 90, description: 'Collaboration directe' },
                { channel: 'Dashboard', usage: 100, satisfaction: 95, description: 'Visibilité temps réel' },
                { channel: 'Rapports', usage: 80, satisfaction: 88, description: 'Suivi détaillé' }
              ].map((channel, index) => (
                <div key={index} className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <div className="font-semibold text-gray-800">{channel.channel}</div>
                      <div className="text-sm text-gray-600">{channel.description}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{channel.satisfaction}%</div>
                      <div className="text-xs text-gray-500">Satisfaction</div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${channel.usage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Usage: {channel.usage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Rocket className="w-6 h-6" />
            TactiCe - La Solution Complète
          </h3>
          <p className="mb-4">
            TactiCe transforme la gestion de projet avec une approche centrée sur la communication 
            et l'intelligence artificielle. Tous vos projets bénéficient d'une visibilité complète 
            et d'un suivi en temps réel.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-sm">Projets dans BZ</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">95%</div>
              <div className="text-sm">Satisfaction Client</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-sm">Planning Automatisé</div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function ChargeProjetTactiCe({ isOpen, onClose }: ChargeProjetTactiCeProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = createSlides();

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
